import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

export default class extends Controller {
  static targets = [
    "canvas",
    "input",
    "fileInput",
    "placeholder",
    "drawTab",
    "uploadTab",
    "drawPanel",
    "uploadPanel",
    "previewModal",
    "previewImage",
    "statusIcon",
    "statusLabel"
  ]

  static values = {
    updateUrl: String,
    maxFileSize: { type: Number, default: 3145728 },
    undefinedText: { type: String, default: "Non définie" },
    emptyError: { type: String, default: "Veuillez dessiner votre signature" }
  }

  connect() {
    this.signaturePadInitialized = false
    this.showTab("draw")
    this.setupVisibilityObserver()
  }

  disconnect() {
    if (this.boundResizeCanvas) {
      window.removeEventListener("resize", this.boundResizeCanvas)
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
    }
  }

  setupVisibilityObserver() {
    // Use IntersectionObserver to detect when canvas becomes visible
    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.signaturePadInitialized) {
          this.initSignaturePad()
        }
      })
    }, { threshold: 0.1 })

    this.visibilityObserver.observe(this.canvasTarget)
  }

  initSignaturePad() {
    // Prevent double initialization
    if (this.signaturePadInitialized) return

    // Check canvas is visible and has dimensions
    if (this.canvasTarget.offsetWidth === 0 || this.canvasTarget.offsetHeight === 0) {
      return
    }

    this.signaturePad = new SignaturePad(this.canvasTarget, {
      backgroundColor: "rgb(255, 255, 255)",
      penColor: "rgb(0, 0, 0)",
      minWidth: 2,
      maxWidth: 4,
      velocityFilterWeight: 0.7
    })

    this.boundResizeCanvas = this.resizeCanvas.bind(this)
    this.resizeCanvas()
    window.addEventListener("resize", this.boundResizeCanvas)

    this.signaturePad.addEventListener("beginStroke", () => {
      this.hidePlaceholder()
      this.updateStatus(true)
    })

    this.signaturePadInitialized = true
  }

  resizeCanvas() {
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    this.canvasTarget.width = this.canvasTarget.offsetWidth * ratio
    this.canvasTarget.height = this.canvasTarget.offsetHeight * ratio
    this.canvasTarget.getContext("2d").scale(ratio, ratio)
    this.signaturePad.clear()
  }

  showTab(tabName) {
    const isDrawTab = tabName === "draw"

    this.drawTabTarget.classList.toggle("bg-blue-600", isDrawTab)
    this.drawTabTarget.classList.toggle("text-white", isDrawTab)
    this.drawTabTarget.classList.toggle("bg-gray-100", !isDrawTab)
    this.drawTabTarget.classList.toggle("text-gray-700", !isDrawTab)

    this.uploadTabTarget.classList.toggle("bg-blue-600", !isDrawTab)
    this.uploadTabTarget.classList.toggle("text-white", !isDrawTab)
    this.uploadTabTarget.classList.toggle("bg-gray-100", isDrawTab)
    this.uploadTabTarget.classList.toggle("text-gray-700", isDrawTab)

    this.drawPanelTarget.classList.toggle("hidden", !isDrawTab)
    this.uploadPanelTarget.classList.toggle("hidden", isDrawTab)
  }

  switchToDrawTab() {
    this.showTab("draw")
  }

  switchToUploadTab() {
    this.showTab("upload")
  }

  clear() {
    if (!this.signaturePad) return

    this.signaturePad.clear()
    this.inputTarget.value = ""
    this.showPlaceholder()
    this.updateStatus(false)
    this.canvasTarget.focus()
  }

  hidePlaceholder() {
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("hidden")
    }
  }

  showPlaceholder() {
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.remove("hidden")
    }
  }

  updateStatus(isSigned) {
    if (!this.hasStatusIconTarget || !this.hasStatusLabelTarget) return

    const badge = this.statusLabelTarget.parentElement
    if (isSigned) {
      this.statusIconTarget.classList.remove("text-gray-400")
      this.statusIconTarget.classList.add("text-green-500")
      this.statusLabelTarget.textContent = "Prête"
      badge.classList.remove("bg-gray-100")
      badge.classList.add("bg-green-100")
    } else {
      this.statusIconTarget.classList.remove("text-green-500")
      this.statusIconTarget.classList.add("text-gray-400")
      this.statusLabelTarget.textContent = this.undefinedTextValue
      badge.classList.remove("bg-green-100")
      badge.classList.add("bg-gray-100")
    }
  }

  save(event) {
    event.preventDefault()

    if (!this.signaturePad || this.signaturePad.isEmpty()) {
      this.showError(this.emptyErrorValue)
      this.canvasTarget.focus()
      return
    }

    this.showPreview()
  }

  showPreview() {
    const originalDataUrl = this.signaturePad.toDataURL("image/png", 1.0)
    const img = new Image()

    img.onload = () => {
      const targetWidth = 1600
      const ratio = img.height / img.width
      const targetHeight = Math.round(targetWidth * ratio)

      const offscreenCanvas = document.createElement("canvas")
      offscreenCanvas.width = targetWidth
      offscreenCanvas.height = targetHeight
      const ctx = offscreenCanvas.getContext("2d")

      // Enable high-quality image rendering
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = "high"

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

      const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight)
      const data = imageData.data
      for (let i = 0; i < data.length; i += 4) {
        const avg = (data[i] + data[i + 1] + data[i + 2]) / 3
        data[i] = data[i + 1] = data[i + 2] = avg
      }
      ctx.putImageData(imageData, 0, 0)

      const grayResizedDataUrl = offscreenCanvas.toDataURL("image/png", 1.0)
      this.inputTarget.value = grayResizedDataUrl

      if (this.hasPreviewImageTarget) {
        this.previewImageTarget.src = grayResizedDataUrl
      }

      if (this.hasPreviewModalTarget) {
        this.previewModalTarget.classList.remove("hidden")
      }
    }

    img.src = originalDataUrl
  }

  cancelPreview() {
    if (this.hasPreviewModalTarget) {
      this.previewModalTarget.classList.add("hidden")
    }
  }

  confirmAndSubmit() {
    if (this.hasPreviewModalTarget) {
      this.previewModalTarget.classList.add("hidden")
    }
    this.element.submit()
  }

  closeOnBackdrop(event) {
    if (event.target === this.previewModalTarget) {
      this.cancelPreview()
    }
  }

  preventPropagation(event) {
    event.stopPropagation()
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    if (!file.type.match(/^image\/(png|jpeg|jpg|webp)$/)) {
      this.showError("Format non supporté. Utilisez PNG, JPEG ou WebP.")
      this.fileInputTarget.value = ""
      return
    }

    if (file.size > this.maxFileSizeValue) {
      this.showError("Fichier trop volumineux. Maximum 3 MB.")
      this.fileInputTarget.value = ""
      return
    }

    this.element.submit()
  }

  showError(message) {
    const existingError = this.element.querySelector(".signature-error-toast")
    if (existingError) {
      existingError.remove()
    }

    const toast = document.createElement("div")
    toast.className = "signature-error-toast fixed top-4 right-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-md shadow-lg z-50 flex items-center gap-2"
    toast.setAttribute("role", "alert")
    toast.innerHTML = `
      <i class="fas fa-exclamation-circle"></i>
      <span>${message}</span>
    `
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 4000)
  }
}
